import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { 
  IconBrush, IconCheck, IconAlertTriangle, IconUser, IconNotebook, 
  IconListDetails, IconBell, IconTool, IconSearch
} from "@tabler/icons-react";
import apiClient from "../helpers/ApiClient";

export default function HousekeepingPage() {
  const [tab, setTab] = useState("rooms"); // rooms, tasks, requests, maintenance, lostfound
  const [loading, setLoading] = useState(false);

  // Data
  const [rooms, setRooms] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [requests, setRequests] = useState([]);
  const [issues, setIssues] = useState([]);
  const [lostItems, setLostItems] = useState([]);

  // Modals
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  
  const [taskForm, setTaskForm] = useState({ task_type: "Checkout", assigned_to: "", notes: "" });
  const [requestForm, setRequestForm] = useState({ room_id: "", request_item: "" });
  const [maintenanceForm, setMaintenanceForm] = useState({ room_id: "", issue_description: "", priority: "Medium", reported_by: "" });
  const [lostForm, setLostForm] = useState({ room_id: "", item_description: "" });

  const [isGeneralModalOpen, setIsGeneralModalOpen] = useState(false);
  const [modalType, setModalType] = useState(""); // request, maintenance, lost

  useEffect(() => {
    fetchTabData();
  }, [tab]);

  const fetchTabData = async () => {
    setLoading(true);
    try {
      if (tab === "rooms") {
        const res = await apiClient.get("/rooms");
        setRooms(res.data);
      } else if (tab === "tasks") {
        const res = await apiClient.get("/rooms/housekeeping/tasks");
        setTasks(res.data);
      } else if (tab === "requests") {
        const [reqRes, roomRes] = await Promise.all([
          apiClient.get("/rooms/guest-requests"),
          apiClient.get("/rooms")
        ]);
        setRequests(reqRes.data);
        setRooms(roomRes.data);
      } else if (tab === "maintenance") {
        const [maintRes, roomRes] = await Promise.all([
          apiClient.get("/rooms/maintenance"),
          apiClient.get("/rooms")
        ]);
        setIssues(maintRes.data);
        setRooms(roomRes.data);
      } else if (tab === "lostfound") {
        const [lostRes, roomRes] = await Promise.all([
          apiClient.get("/rooms/lost-found"),
          apiClient.get("/rooms")
        ]);
        setLostItems(lostRes.data);
        setRooms(roomRes.data);
      }
    } catch (e) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRoomStatus = async (roomId, newStatus) => {
    try {
      await apiClient.patch(`/rooms/${roomId}/housekeeping`, {
        housekeeping_status: newStatus
      });
      toast.success(`Room status updated to ${newStatus}`);
      fetchTabData();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const handleOpenTaskModal = (room) => {
    setSelectedRoom(room);
    setTaskForm({ task_type: "Checkout", assigned_to: "", notes: "" });
    setIsTaskModalOpen(true);
  };

  const handleSaveTask = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post(`/rooms/housekeeping/tasks`, {
        room_id: selectedRoom.id,
        task_type: taskForm.task_type,
        assigned_to: taskForm.assigned_to,
        notes: taskForm.notes
      });
      toast.success("Housekeeping task assigned");
      setIsTaskModalOpen(false);
      fetchTabData();
    } catch (e) {
      toast.error("Failed to assign housekeeping task");
    }
  };

  const handleUpdateTaskStatus = async (id, status) => {
    try {
      const inspected_by = status === "Inspected" ? "Supervisor" : null;
      await apiClient.patch(`/rooms/housekeeping/tasks/${id}`, { status, inspected_by });
      toast.success(`Task marked as ${status}`);
      fetchTabData();
    } catch (e) {
      toast.error("Failed to update task");
    }
  };

  const handleGeneralSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalType === "request") {
        await apiClient.post("/rooms/guest-requests", requestForm);
        toast.success("Request logged");
      } else if (modalType === "maintenance") {
        await apiClient.post("/rooms/maintenance", maintenanceForm);
        toast.success("Issue reported");
      } else if (modalType === "lost") {
        await apiClient.post("/rooms/lost-found", lostForm);
        toast.success("Lost & Found item logged");
      }
      setIsGeneralModalOpen(false);
      fetchTabData();
    } catch (e) {
      toast.error("Failed to save record");
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case "Clean": case "Ready": case "Fulfilled": case "Resolved": case "Claimed":
        return "bg-green-50 text-green-600 dark:bg-green-950/40 dark:text-green-400";
      case "Dirty": case "Occupied": case "Pending": case "High":
        return "bg-yellow-50 text-yellow-600 dark:bg-yellow-950/40 dark:text-yellow-400";
      case "Inspected": case "Found": case "Low":
        return "bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400";
      case "DND": case "Out of Order": case "Critical":
        return "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400";
      default:
        return "bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-zinc-400";
    }
  };

  return (
    <div className="p-6 min-h-screen bg-restro-green-light dark:bg-restro-gray text-restro-text dark:text-white">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">Housekeeping Management</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Track cleaning progress, assignments, maintenance, and guest requests.</p>
        </div>
        {tab !== "rooms" && tab !== "tasks" && (
          <button
            onClick={() => {
              setModalType(tab === "requests" ? "request" : tab === "maintenance" ? "maintenance" : "lost");
              setIsGeneralModalOpen(true);
            }}
            className="px-5 py-3 rounded-2xl bg-restro-green hover:bg-restro-green-button-hover text-white font-bold flex items-center gap-2 shadow-sm transition"
          >
            Log New {tab === "requests" ? "Request" : tab === "maintenance" ? "Issue" : "Item"}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100 dark:border-zinc-800 gap-6 mb-8 overflow-x-auto">
        {[
          { id: "rooms", label: "Room Status", icon: <IconBrush size={18} /> },
          { id: "tasks", label: "Tasks & Inspections", icon: <IconListDetails size={18} /> },
          { id: "requests", label: "Guest Requests", icon: <IconBell size={18} /> },
          { id: "maintenance", label: "Maintenance", icon: <IconTool size={18} /> },
          { id: "lostfound", label: "Lost & Found", icon: <IconSearch size={18} /> }
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`py-4 px-2 text-sm font-semibold border-b-2 -mb-px flex items-center gap-2 whitespace-nowrap transition ${
              tab === t.id 
                ? "border-restro-green text-restro-green" 
                : "border-transparent text-gray-400 hover:text-gray-600"
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-restro-green"></div>
        </div>
      ) : (
        <div className="bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 rounded-3xl shadow-sm overflow-hidden">
          
          {/* TAB: ROOM STATUS */}
          {tab === "rooms" && (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-zinc-800 border-b border-gray-100 dark:border-zinc-800">
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Room Number</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Type</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Status</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px] text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                  {rooms.map(room => (
                    <tr key={room.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition-colors">
                      <td className="p-4 font-extrabold text-gray-900 dark:text-white">{room.name}</td>
                      <td className="p-4 text-gray-500 dark:text-zinc-400">{room.room_type_name}</td>
                      <td className="p-4">
                        <select
                          value={room.housekeeping_status}
                          onChange={(e) => handleUpdateRoomStatus(room.id, e.target.value)}
                          className={`px-3 py-1.5 rounded-full text-xs font-bold border-0 focus:ring-1 focus:ring-restro-green cursor-pointer ${getStatusColor(room.housekeeping_status)}`}
                        >
                          <option value="Clean">Clean</option>
                          <option value="Dirty">Dirty</option>
                          <option value="Occupied">Occupied</option>
                          <option value="Vacant">Vacant</option>
                          <option value="Inspected">Inspected</option>
                          <option value="Ready">Ready</option>
                          <option value="DND">Do Not Disturb</option>
                          <option value="Out of Order">Out of Order</option>
                        </select>
                      </td>
                      <td className="p-4 text-right">
                        <button
                          onClick={() => handleOpenTaskModal(room)}
                          className="px-3.5 py-1.5 rounded-xl border border-gray-200 dark:border-zinc-800 hover:bg-gray-50 dark:hover:bg-zinc-800 text-xs font-semibold transition"
                        >
                          Assign Task
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB: TASKS */}
          {tab === "tasks" && (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-zinc-800 border-b border-gray-100 dark:border-zinc-800">
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Room</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Task Type</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Housekeeper</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Status</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Date</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px] text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                  {tasks.map(task => (
                    <tr key={task.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition-colors">
                      <td className="p-4 font-extrabold text-gray-900 dark:text-white">{task.room_name}</td>
                      <td className="p-4 font-semibold text-gray-700 dark:text-zinc-300">{task.task_type}</td>
                      <td className="p-4 text-gray-500 dark:text-zinc-400">{task.assigned_to || "-"}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusColor(task.status)}`}>{task.status}</span>
                        {task.inspected_by && <div className="text-[10px] text-gray-400 mt-1">By: {task.inspected_by}</div>}
                      </td>
                      <td className="p-4 text-gray-500 dark:text-zinc-400">{new Date(task.created_at).toLocaleString()}</td>
                      <td className="p-4 text-right">
                        {task.status === "Pending" && (
                          <button onClick={() => handleUpdateTaskStatus(task.id, "Completed")} className="text-xs font-bold text-restro-green hover:underline">Mark Done</button>
                        )}
                        {task.status === "Completed" && (
                          <button onClick={() => handleUpdateTaskStatus(task.id, "Inspected")} className="text-xs font-bold text-blue-500 hover:underline">Inspect & Approve</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB: REQUESTS */}
          {tab === "requests" && (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-zinc-800 border-b border-gray-100 dark:border-zinc-800">
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Room</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Requested Item</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Status</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Time</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px] text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                  {requests.map(req => (
                    <tr key={req.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition-colors">
                      <td className="p-4 font-extrabold text-gray-900 dark:text-white">{req.room_name}</td>
                      <td className="p-4 font-semibold text-gray-700 dark:text-zinc-300">{req.request_item}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusColor(req.status)}`}>{req.status}</span>
                      </td>
                      <td className="p-4 text-gray-500 dark:text-zinc-400">{new Date(req.requested_at).toLocaleString()}</td>
                      <td className="p-4 text-right">
                        {req.status === "Pending" && (
                          <button onClick={async () => {
                            await apiClient.patch(`/rooms/guest-requests/${req.id}`, { status: "Fulfilled" });
                            fetchTabData();
                          }} className="text-xs font-bold text-restro-green hover:underline">Mark Fulfilled</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB: MAINTENANCE */}
          {tab === "maintenance" && (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-zinc-800 border-b border-gray-100 dark:border-zinc-800">
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Room</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Issue</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Priority</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Status</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Time</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px] text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                  {issues.map(iss => (
                    <tr key={iss.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition-colors">
                      <td className="p-4 font-extrabold text-gray-900 dark:text-white">{iss.room_name}</td>
                      <td className="p-4 font-semibold text-gray-700 dark:text-zinc-300">{iss.issue_description}</td>
                      <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusColor(iss.priority)}`}>{iss.priority}</span></td>
                      <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusColor(iss.status)}`}>{iss.status}</span></td>
                      <td className="p-4 text-gray-500 dark:text-zinc-400">{new Date(iss.created_at).toLocaleString()}</td>
                      <td className="p-4 text-right">
                        {iss.status === "Pending" && (
                          <button onClick={async () => {
                            await apiClient.patch(`/rooms/maintenance/${iss.id}`, { status: "Resolved", resolved_by: "Engineer" });
                            fetchTabData();
                          }} className="text-xs font-bold text-restro-green hover:underline">Resolve</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB: LOST & FOUND */}
          {tab === "lostfound" && (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-zinc-800 border-b border-gray-100 dark:border-zinc-800">
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Room</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Item Description</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Status</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px]">Found Date</th>
                    <th className="p-4 font-bold text-gray-500 uppercase tracking-wider text-[11px] text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                  {lostItems.map(item => (
                    <tr key={item.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition-colors">
                      <td className="p-4 font-extrabold text-gray-900 dark:text-white">{item.room_name}</td>
                      <td className="p-4 font-semibold text-gray-700 dark:text-zinc-300">{item.item_description}</td>
                      <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusColor(item.status)}`}>{item.status}</span></td>
                      <td className="p-4 text-gray-500 dark:text-zinc-400">{new Date(item.found_date).toLocaleString()}</td>
                      <td className="p-4 text-right">
                        {item.status === "Found" && (
                          <button onClick={async () => {
                            const name = prompt("Enter claimant name:");
                            if(name) {
                              await apiClient.patch(`/rooms/lost-found/${item.id}`, { status: "Claimed", claimed_by: name });
                              fetchTabData();
                            }
                          }} className="text-xs font-bold text-blue-500 hover:underline">Mark Claimed</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </div>
      )}

      {/* Task Assignment Modal */}
      {isTaskModalOpen && selectedRoom && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-zinc-950 border border-gray-100 dark:border-zinc-800 rounded-3xl w-full max-w-md shadow-2xl p-6 space-y-6 animate-fade-in">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">Task for Room {selectedRoom.name}</h3>
              <button onClick={() => setIsTaskModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
            </div>
            <form onSubmit={handleSaveTask} className="space-y-4">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Task Type</label>
                <select
                  value={taskForm.task_type}
                  onChange={e => setTaskForm({ ...taskForm, task_type: e.target.value })}
                  className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm focus:outline-none"
                >
                  <option value="Checkout">Checkout Cleaning</option>
                  <option value="Stay-over">Stay-over Cleaning</option>
                  <option value="Deep Cleaning">Deep Cleaning</option>
                  <option value="Turndown Service">Turndown Service</option>
                  <option value="VIP Prep">VIP / Special Prep</option>
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Assigned Housekeeper</label>
                <input
                  type="text"
                  required
                  value={taskForm.assigned_to}
                  onChange={e => setTaskForm({ ...taskForm, assigned_to: e.target.value })}
                  className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none"
                  placeholder="e.g. Ram Prasad"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Extra Notes</label>
                <input
                  type="text"
                  value={taskForm.notes}
                  onChange={e => setTaskForm({ ...taskForm, notes: e.target.value })}
                  className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none"
                  placeholder="Optional instructions..."
                />
              </div>
              <button type="submit" className="w-full py-4 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white transition">Assign Task</button>
            </form>
          </div>
        </div>
      )}

      {/* General Modal for Requests, Maintenance, Lost & Found */}
      {isGeneralModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-zinc-950 border border-gray-100 dark:border-zinc-800 rounded-3xl w-full max-w-md shadow-2xl p-6 space-y-6 animate-fade-in">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
                Log New {modalType === "request" ? "Guest Request" : modalType === "maintenance" ? "Maintenance Issue" : "Lost & Found Item"}
              </h3>
              <button onClick={() => setIsGeneralModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
            </div>
            <form onSubmit={handleGeneralSubmit} className="space-y-4">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Room</label>
                <select
                  required
                  onChange={e => {
                    const id = e.target.value;
                    if(modalType==="request") setRequestForm({...requestForm, room_id: id});
                    if(modalType==="maintenance") setMaintenanceForm({...maintenanceForm, room_id: id});
                    if(modalType==="lost") setLostForm({...lostForm, room_id: id});
                  }}
                  className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm focus:outline-none"
                >
                  <option value="">Select Room...</option>
                  {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>

              {modalType === "request" && (
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Request Item</label>
                  <input
                    type="text" required
                    value={requestForm.request_item}
                    onChange={e => setRequestForm({ ...requestForm, request_item: e.target.value })}
                    className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none"
                    placeholder="e.g. Extra towels, Pillows, Blankets"
                  />
                </div>
              )}

              {modalType === "maintenance" && (
                <>
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Issue Description</label>
                    <input
                      type="text" required
                      value={maintenanceForm.issue_description}
                      onChange={e => setMaintenanceForm({ ...maintenanceForm, issue_description: e.target.value })}
                      className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none"
                      placeholder="e.g. Broken AC, Leaky faucet"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Priority</label>
                    <select
                      value={maintenanceForm.priority}
                      onChange={e => setMaintenanceForm({ ...maintenanceForm, priority: e.target.value })}
                      className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm focus:outline-none"
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  </div>
                </>
              )}

              {modalType === "lost" && (
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Item Description</label>
                  <input
                    type="text" required
                    value={lostForm.item_description}
                    onChange={e => setLostForm({ ...lostForm, item_description: e.target.value })}
                    className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none"
                    placeholder="e.g. Gold watch, Blue jacket"
                  />
                </div>
              )}

              <button type="submit" className="w-full py-4 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white transition">Save Record</button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
